/**
 * Slice 45 — Supply forecast table (leave-out × guests, past-counts fallback).
 */
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSupplyForecast } from "@/hooks/useSupplyForecast";
import { formatPullLine, type SupplyForecastRow } from "@/lib/supplyForecast";

interface InventoryPredictionsTableProps {
  propertyId: string;
  /** Opens the leave-out item picker. Never opens the first row. */
  onGoToLeaveOutRules?: () => void;
  unsetCount?: number;
  allItemsSet?: boolean;
}

function statusPill(status: SupplyForecastRow["status"]) {
  switch (status) {
    case "ok":
      return "bg-[#e2eeed] text-[#15776d]";
    case "order_soon":
      return "bg-[#fff3dc] text-[#9a5a06]";
    case "critical":
      return "bg-[#fdecec] text-[#dc2626]";
    case "out":
      return "bg-[#dc2626] text-white";
    default:
      return "bg-[#f1f5f7] text-[#64748b]";
  }
}

function statusLabel(status: SupplyForecastRow["status"]) {
  switch (status) {
    case "ok":
      return "OK";
    case "order_soon":
      return "Order soon";
    case "critical":
      return "Critical";
    case "out":
      return "Out";
    default:
      return "Need data";
  }
}

function SourceChip({ source }: { source: SupplyForecastRow["source"] }) {
  if (source === "leave_out") {
    return (
      <span className="rounded-full border border-[#cfe5e2] px-2 py-0.5 text-[11.5px] font-bold text-[#15776d]">
        Leave-out
      </span>
    );
  }
  return (
    <span className="rounded-full bg-[#f1f5f7] px-2 py-0.5 text-[11.5px] font-bold text-[#64748b]">
      Past counts
    </span>
  );
}

export default function InventoryPredictionsTable({
  propertyId,
  onGoToLeaveOutRules,
  unsetCount = 0,
  allItemsSet = false,
}: InventoryPredictionsTableProps) {
  const { rows, stays, hasAnyLeaveOutRule, prefersLeaveOut, isLoading } =
    useSupplyForecast(propertyId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const subtitle = useMemo(() => {
    if (!hasAnyLeaveOutRule) return null;
    if (prefersLeaveOut && rows.every((r) => r.source === "leave_out" || r.status === "insufficient_data")) {
      return "From leave-out rules + upcoming guests";
    }
    if (prefersLeaveOut) return "From leave-out rules + upcoming guests";
    return "From past cleaner counts";
  }, [hasAnyLeaveOutRule, prefersLeaveOut, rows]);

  const allPast = rows.length > 0 && rows.every((r) => r.source === "past_counts");

  if (isLoading) {
    return (
      <div
        id="inventory-predictions"
        className="scroll-mt-4 overflow-hidden rounded-[16px] border border-[#e6edf1] bg-white px-5 py-5"
      >
        <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">Supply forecast</div>
        <p className="mt-2 text-[14px] font-medium text-[#8494a2]">Loading…</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        id="inventory-predictions"
        className="scroll-mt-4 overflow-hidden rounded-[16px] border border-[#e6edf1] bg-white"
      >
        <div className="px-5 py-4">
          <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">Supply forecast</div>
          <p className="mt-1 text-[14px] font-medium text-[#64748b]">
            No inventory items tracked yet. Add items above.
          </p>
        </div>
      </div>
    );
  }

  if (!hasAnyLeaveOutRule && rows.every((r) => r.status === "insufficient_data")) {
    return (
      <div
        id="inventory-predictions"
        className="scroll-mt-4 overflow-hidden rounded-[16px] border border-[#e6edf1] bg-white"
      >
        <div className="flex flex-col gap-4 px-5 py-5">
          <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">Supply forecast</div>
          <p className="max-w-xl text-[15px] font-medium leading-relaxed text-[#475569]">
            Set leave-out rules so we can plan by guest count — how many to leave in each place for each stay.
          </p>
          {onGoToLeaveOutRules ? (
            <button
              type="button"
              onClick={onGoToLeaveOutRules}
              className="flex h-[42px] w-fit items-center rounded-[11px] bg-[#24a89a] px-[18px] text-[14.5px] font-bold text-white hover:bg-[#15776d]"
            >
              Set leave-out
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const noUpcoming = stays.length === 0 && prefersLeaveOut;
  const showProjectionCols = !noUpcoming;

  return (
    <div
      id="inventory-predictions"
      className="scroll-mt-4 overflow-hidden rounded-[16px] border border-[#e6edf1] bg-white"
    >
      <div className="flex items-end gap-3 border-b border-[#e6edf1] px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">Supply forecast</div>
          <div className="mt-0.5 text-[13px] font-medium text-[#64748b]">
            {allPast
              ? "From past cleaner counts"
              : subtitle ?? "From leave-out rules + upcoming guests"}
            {unsetCount > 0 ? ` · ${unsetCount} item${unsetCount === 1 ? "" : "s"} not set` : ""}
          </div>
        </div>
        {onGoToLeaveOutRules ? (
          <button
            type="button"
            onClick={onGoToLeaveOutRules}
            className={cn(
              "flex h-[42px] shrink-0 items-center rounded-[11px] px-[18px] text-[14.5px] font-bold",
              allItemsSet
                ? "border border-[#e6edf1] text-[#15776d] hover:bg-[#f4f7f9]"
                : "bg-[#24a89a] text-white hover:bg-[#15776d]",
            )}
          >
            {allItemsSet ? "Edit leave-out" : "Set leave-out"}
          </button>
        ) : (
          <div className="shrink-0 text-[13px] font-medium text-[#8494a2]">
            {noUpcoming
              ? "No upcoming stays"
              : `${stays.length} stay${stays.length !== 1 ? "s" : ""} · 30 days`}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <div
          className={cn(
            "grid gap-4 border-b border-[#eef2f5] px-5 py-2.5 text-[12.5px] font-bold text-[#8494a2]",
            showProjectionCols
              ? "grid-cols-[minmax(0,1fr)_64px_160px_64px_230px_110px]"
              : "grid-cols-[minmax(0,1fr)_64px_160px]",
          )}
        >
          <div>Item</div>
          <div>Now</div>
          <div>Out next 30d</div>
          {showProjectionCols ? (
            <>
              <div>After</div>
              <div>Order by</div>
              <div>Status</div>
            </>
          ) : null}
        </div>

        {rows.map((row) => {
          const open = expandedId === row.item_id;
          const canExpand = row.source === "leave_out" && row.pulls.length > 0;
          return (
            <div key={row.item_id} className={cn(open && "bg-[#fafcfd]")}>
              <button
                type="button"
                disabled={!canExpand}
                onClick={() => setExpandedId(open ? null : row.item_id)}
                className={cn(
                  "grid w-full gap-4 border-b border-[#eef2f5] px-5 py-3.5 text-left items-center",
                  showProjectionCols
                    ? "grid-cols-[minmax(0,1fr)_64px_160px_64px_230px_110px]"
                    : "grid-cols-[minmax(0,1fr)_64px_160px]",
                  open && "bg-white shadow-[inset_3px_0_0_#24a89a]",
                  canExpand && "hover:bg-[#f8fbfc]",
                )}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="truncate text-[15.5px] font-bold text-[#0f1720]">{row.item_name}</span>
                  <SourceChip source={row.source} />
                  {canExpand ? (
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 text-[#8494a2] transition-transform",
                        open && "rotate-180",
                      )}
                    />
                  ) : null}
                </div>
                <div className="text-[16px] font-bold tabular-nums">{row.current_stock}</div>
                <div className="flex items-baseline gap-1.5">
                  {noUpcoming ? (
                    <span className="text-[13.5px] font-medium text-[#8494a2]">No upcoming stays</span>
                  ) : row.out_next_30d == null ? (
                    <span className="text-[13.5px] font-medium text-[#8494a2]">—</span>
                  ) : (
                    <>
                      <span className="text-[16px] font-bold tabular-nums">
                        {row.source === "past_counts" ? `~${row.out_next_30d}` : row.out_next_30d}
                      </span>
                      {row.source === "leave_out" && row.stay_count > 0 ? (
                        <span className="text-[12.5px] font-medium text-[#8494a2]">
                          from {row.stay_count} stay{row.stay_count !== 1 ? "s" : ""}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
                {showProjectionCols ? (
                  <>
                    <div className="text-[16px] font-bold tabular-nums">
                      {row.after == null ? "—" : row.source === "past_counts" ? `~${row.after}` : row.after}
                    </div>
                    <div
                      className={cn(
                        "text-[14px] font-medium",
                        row.order_by_label === "OK" ? "text-[#64748b]" : "font-bold text-[#0f1720]",
                        (row.status === "critical" || row.status === "out") && "text-[#dc2626]",
                      )}
                    >
                      {row.order_by_label}
                    </div>
                    <div>
                      <span
                        className={cn(
                          "inline-flex rounded-full px-3 py-1.5 text-[13px] font-bold",
                          statusPill(row.status),
                        )}
                      >
                        {statusLabel(row.status)}
                      </span>
                    </div>
                  </>
                ) : null}
              </button>

              {open && canExpand ? (
                <div className="border-b border-[#eef2f5] px-5 pb-4 pt-1">
                  <div className="mb-1 grid grid-cols-[minmax(0,1fr)_140px_140px] gap-4 border-b border-[#eef2f5] py-2.5 text-[12.5px] font-bold text-[#8494a2]">
                    <div>Upcoming pulls</div>
                    <div>Closet after</div>
                    <div />
                  </div>
                  {row.pulls.map((pull) => (
                    <div
                      key={pull.stay_id}
                      className="grid grid-cols-[minmax(0,1fr)_140px_140px] items-center gap-4 border-b border-[#eef2f5] py-2.5 last:border-b-0"
                    >
                      <div
                        className={cn(
                          "text-[14.5px] font-medium",
                          pull.estimated ? "text-[#64748b]" : "text-[#0f1720]",
                        )}
                      >
                        {formatPullLine(pull)}
                      </div>
                      <div
                        className={cn(
                          "text-[14.5px] font-bold tabular-nums",
                          pull.hits_low || pull.closet_after <= row.threshold_count
                            ? "text-[#9a5a06]"
                            : "text-[#0f1720]",
                        )}
                      >
                        {pull.closet_after}
                      </div>
                      <div className="justify-self-start">
                        {pull.hits_low ? (
                          <span className="text-[12.5px] font-bold text-[#9a5a06]">Low here</span>
                        ) : pull.estimated ? (
                          <span className="rounded-full bg-[#f1f5f7] px-2 py-0.5 text-[11.5px] font-bold text-[#64748b]">
                            Estimated
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  <div className="mt-2.5 flex flex-wrap gap-4 text-[13px] font-medium text-[#64748b]">
                    <div className="flex-1">Low at {row.threshold_count} · set on Inventory</div>
                    {onGoToLeaveOutRules ? (
                      <button
                        type="button"
                        onClick={onGoToLeaveOutRules}
                        className="font-bold text-[#15776d] hover:underline"
                      >
                        Edit leave-out →
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {row.source === "past_counts" && onGoToLeaveOutRules ? (
                <div className="flex justify-end border-b border-[#eef2f5] px-5 py-2">
                  <button
                    type="button"
                    onClick={onGoToLeaveOutRules}
                    className="text-[13px] font-bold text-[#15776d] hover:underline"
                  >
                    Set leave-out rule →
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Phone cards */}
      <div className="flex flex-col gap-0 md:hidden">
        {rows.map((row) => {
          const open = expandedId === row.item_id;
          const canExpand = row.source === "leave_out" && row.pulls.length > 0;
          return (
            <div key={row.item_id} className="border-b border-[#eef2f5] px-4 py-3.5">
              <button
                type="button"
                className="flex w-full items-start gap-2 text-left"
                disabled={!canExpand}
                onClick={() => setExpandedId(open ? null : row.item_id)}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-bold text-[#0f1720]">{row.item_name}</div>
                  <div className="mt-1">
                    <SourceChip source={row.source} />
                  </div>
                </div>
                {showProjectionCols ? (
                  <span
                    className={cn(
                      "rounded-full px-3.5 py-2 text-[14px] font-bold",
                      statusPill(row.status),
                    )}
                  >
                    {statusLabel(row.status)}
                  </span>
                ) : null}
              </button>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[12px] font-bold text-[#8494a2]">Now</div>
                  <div className="text-[20px] font-bold tabular-nums">{row.current_stock}</div>
                </div>
                <div>
                  <div className="text-[12px] font-bold text-[#8494a2]">Out next 30d</div>
                  <div className="text-[20px] font-bold tabular-nums">
                    {noUpcoming
                      ? "—"
                      : row.out_next_30d == null
                        ? "—"
                        : row.source === "past_counts"
                          ? `~${row.out_next_30d}`
                          : row.out_next_30d}
                  </div>
                </div>
                {showProjectionCols ? (
                  <div>
                    <div className="text-[12px] font-bold text-[#8494a2]">After</div>
                    <div className="text-[20px] font-bold tabular-nums">
                      {row.after == null ? "—" : row.source === "past_counts" ? `~${row.after}` : row.after}
                    </div>
                  </div>
                ) : null}
              </div>
              {showProjectionCols ? (
                <div
                  className={cn(
                    "mt-2.5 border-t border-[#eef2f5] pt-2.5 text-[14px]",
                    row.order_by_label === "OK"
                      ? "font-medium text-[#64748b]"
                      : "font-bold text-[#0f1720]",
                    (row.status === "critical" || row.status === "out") && "text-[#dc2626]",
                  )}
                >
                  Order by · {row.order_by_label}
                </div>
              ) : (
                <div className="mt-2 text-[13.5px] font-medium text-[#8494a2]">No upcoming stays</div>
              )}
              {open && canExpand ? (
                <div className="mt-3 space-y-2 border-t border-[#eef2f5] pt-3">
                  {row.pulls.map((pull) => (
                    <div key={pull.stay_id} className="flex justify-between gap-2 text-[13.5px]">
                      <span className={cn("font-medium", pull.estimated && "text-[#64748b]")}>
                        {formatPullLine(pull)}
                      </span>
                      <span
                        className={cn(
                          "shrink-0 font-bold tabular-nums",
                          pull.hits_low && "text-[#9a5a06]",
                        )}
                      >
                        → {pull.closet_after}
                      </span>
                    </div>
                  ))}
                  {onGoToLeaveOutRules ? (
                    <button
                      type="button"
                      onClick={onGoToLeaveOutRules}
                      className="pt-1 text-[13px] font-bold text-[#15776d]"
                    >
                      Edit leave-out →
                    </button>
                  ) : null}
                </div>
              ) : null}
              {row.source === "past_counts" && onGoToLeaveOutRules ? (
                <button
                  type="button"
                  onClick={onGoToLeaveOutRules}
                  className="mt-2 text-[13px] font-bold text-[#15776d]"
                >
                  Set leave-out rule →
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
